import { z } from 'zod'
import { ChargeType } from '@/api/billing/constants'
import { MeterTypeNumeric } from '@/shared/constants'
import { InvoiceStatusNumeric } from '@/features/invoices/constants'
import { ContractFundEntity } from '@/api/billing/credit-note-schemas'
import { PSO, Season } from '../tariffs/constants'

export const updateInvoiceSchema = z.object({
  showMeterReadings: z.boolean(),
  showSavings: z.boolean(),
  poNumber: z.string().nullable(),
  recsGenerated: z.number()
})

export const updateInvoiceChargesSchema = z.array(
  z.object({
    id: z.number().gte(0),
    demandTypeId: z.number().nullable(),
    seasonId: z.number().optional(),
    description: z.string().optional(),
    hidden: z.boolean().optional(),
    quantity: z.number().optional(),
    rateInCents: z.number().optional()
  })
)

export const addInvoiceChargesSchema = z.array(z.object({
  invoiceId: z.number().gt(0),
  categoryId: z.number().gt(0),
  chargeType: z.nativeEnum(ChargeType),
  description: z.string(),
  rateInCents: z.number().gte(0),
  quantity: z.number().gte(0),
  isHidden: z.boolean(),
  seasonId: z.number().nullish(),
  demandTypeId: z.number().nullish()
}))

export const updateInvoiceCategoriesSchema = z.array(
  z.object({
    id: z.number().gt(0),
    hidden: z.boolean().optional(),
  })
)

export const invoiceSchema = z.object({
  id: z.number().gte(0),
  assetCode: z.string(),
  projectCode: z.number(),
  projectName: z.string(),
  isManualInvoice: z.boolean(),
  billingConfigurationId: z.number().gt(0),
  invoiceNumber: z.string(),
  poNumber: z.string().nullable(),
  invoiceDate: z.coerce.date(),
  periodStartDate: z.coerce.date(),
  periodEndDate: z.coerce.date(),
  gridTariff: z.number(),
  pvBlendedTariff: z.number(),
  exportBlendedTariff: z.number(),
  totalEnergyConsumption: z.number(),
  totalGridConsumption: z.number(),
  totalEnergyExported: z.number().nullable(),
  totalCreditedInRands: z.number(),
  totalAmountDueInRands: z.number(),
  totalAmountExcludingTax: z.number(),
  tax: z.number(),
  invoiceNotes: z.string().nullable(),
  taxPercentage: z.number(),
  totalAmountIncludingTax: z.number(),
  totalSavingsToDate: z.number(),
  showMeterReadings: z.boolean(),
  showSavings: z.boolean(),
  dueDate: z.coerce.date(),
  isExportPower: z.boolean(),
  invoiceDateFormat: z.string(),
  invoiceStatusId: z.nativeEnum(InvoiceStatusNumeric),
  recsGenerated: z.number().nullable(),
  invoiceSavings: z.object({
    amount: z.number()
  }).nullable(),

  customer: z.object({
    name: z.string(),
    addressLineOne: z.string().nullable(),
    addressLineTwo: z.string().nullable(),
    suburb: z.string().nullable(),
    city: z.string().nullable(),
    region: z.string().nullable(),
    country: z.string().nullable(),
    vatNumber: z.string().nullable(),

  }),

  contractFundEntity: z.object({
    name: z.string(),
    registrationNumber: z.string(),
    address: z.string(),
    vatNumber: z.string(),
    logoUrl: z.string().nullable(),
    accountName: z.string(),
    bank: z.string(),
    accountNumber: z.string(),
    branchCode: z.string(),
  }),

  categories: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      hidden: z.boolean(),
      features: z.array(z.enum(['hide', 'hide-charges'])),
      charges: z.array(
        z.object({
          id: z.number(),
          chargeType: z.nativeEnum(ChargeType),
          description: z.string(),
          quantity: z.number(),
          isQuantityModified: z.boolean(),
          rateInCents: z.number(),
          isRateModified: z.boolean(),
          hidden: z.boolean().optional(),
          tariffTypeId: z.number().nullable(),
          demandTypeId: z.number().nullable(),
          seasonId: z.number()
        })
      )
    })
  ),

  meterReadings: z.array(
    z.object({
      id: z.number(),
      deviceId: z.string(),
      vcomDeviceId: z.string().nullable(),
      meterType: z.object({
        name: z.string(),
      }),
      meterTypeId: z.nativeEnum(MeterTypeNumeric),
      serialNumber: z.string().nullable(),
      hide: z.boolean(),
      isForwardActiveStartReadingModified: z.boolean(),
      isForwardActiveEndReadingModified: z.boolean(),
      isReverseActiveStartReadingModified: z.boolean(),
      isReverseActiveEndReadingModified: z.boolean(),
      forwardActiveStartReading: z.number(),
      forwardActiveEndReading: z.number(),
      reverseActiveStartReading: z.number(),
      reverseActiveEndReading: z.number(),
    })
  )
})

export const createManualInvoiceSchema = z.object({
  assetCode: z.string(),
  invoiceDate: z.coerce.date()
})
